import { gql, useQuery } from "@apollo/client";
import {
  PackageSize,
  ShipmentStopType,
  ShipmentType,
} from "@api/graphql/generated/generated-types";

const DUPLICATE_SHIPMENT_SOURCE_QUERY = gql`
  query DuplicateShipmentSource($courierId: ID!, $shipmentId: ID!) {
    shipmentById(courierId: $courierId, shipmentId: $shipmentId) {
      id
      courierId
      type
      packageWeightInPounds
      numPackages
      packageSize
      packageDescription
      packageType {
        id
      }
      vehicleType {
        id
      }
      stops {
        type
        companyName
        contactName
        phone
        suite
        instruction
        locationId
        streetAddress
        city
        state
        country
        googlePlaceId
        orderEntrySequenceNumber
      }
      order {
        id
        displayId
        name
        caller
        costCenterId
        endCustomerReferenceNumber
        serviceType {
          id
        }
        endCustomer {
          id
        }
        customFieldValues {
          fieldId
          valueText
          valueNumber
          valueInteger
        }
      }
    }
  }
`;

export interface DuplicateShipmentSourceStop {
  type: ShipmentStopType;
  companyName?: string | null;
  contactName?: string | null;
  phone?: string | null;
  suite?: string | null;
  instruction?: string | null;
  locationId?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  googlePlaceId?: string | null;
  orderEntrySequenceNumber?: number | null;
}

export interface DuplicateShipmentSource {
  id: string;
  courierId: string;
  type: ShipmentType;
  packageWeightInPounds?: number | null;
  numPackages?: number | null;
  packageSize?: PackageSize | null;
  packageDescription?: string | null;
  packageType?: { id: string } | null;
  vehicleType?: { id: string } | null;
  stops: DuplicateShipmentSourceStop[];
  order: {
    id: string;
    displayId: number;
    name?: string | null;
    caller?: string | null;
    costCenterId?: string | null;
    endCustomerReferenceNumber?: string | null;
    serviceType?: { id: string } | null;
    endCustomer: { id: string };
    customFieldValues: Array<{
      fieldId: string;
      valueText?: string | null;
      valueNumber?: number | null;
      valueInteger?: number | null;
    }>;
  };
}

interface DuplicateShipmentSourceQueryData {
  shipmentById: DuplicateShipmentSource | null;
}

interface DuplicateShipmentSourceQueryVariables {
  courierId: string;
  shipmentId: string;
}

interface UseDuplicateShipmentSourceOptions {
  shipmentId: string;
  courierId: string | undefined;
  skip?: boolean;
}

export function useDuplicateShipmentSource({
  shipmentId,
  courierId,
  skip = false,
}: UseDuplicateShipmentSourceOptions) {
  const { data, loading, error } = useQuery<
    DuplicateShipmentSourceQueryData,
    DuplicateShipmentSourceQueryVariables
  >(DUPLICATE_SHIPMENT_SOURCE_QUERY, {
    variables: { courierId: courierId!, shipmentId },
    skip: skip || !courierId,
    fetchPolicy: "network-only",
  });

  const shipment = data?.shipmentById ?? undefined;

  return {
    shipment,
    loading,
    error,
    queryData: data,
  };
}
